import ActionButton from "@/components/buttons/ActionButton";
import ContactsEmailAutoComplete from "@/components/form/components/ContactsEmailAutoComplete";
import HiddenInput from "@/components/form/components/HiddenInput";
import { convertObjectToBlob, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { App as AntApp, Card, Form, Modal, Select, Upload } from "antd";
import { RcFile } from "antd/lib/upload";
import { UploadFile } from "antd/lib/upload/interface";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HttpContentType } from "../../../../common/api/http";
import {
  useCreateDocumentSignature,
  useGetClientUserAccounts,
  useResendDocumentSignature,
} from "../../../../common/api/queries";
import { FileUploadAccept, ModalSizes } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { useHasPermissions } from "../../../auth/hooks";
import { DocSignatureType } from "../../../docsignature/enums";
import { DocSignature, InitDocSignature } from "../../../docsignature/types";
import { Client } from "../../types";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const loadPdfAsImages = async (pdfUrl: string) => {
  const loadingTask = pdfjsLib.getDocument({
    url: pdfUrl,
    verbosity: pdfjsLib.VerbosityLevel.ERRORS,
  });
  const pdf = await loadingTask.promise;
  const images: string[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d")!;

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
      canvasContext: context,
      viewport,
      canvas,
    }).promise;

    images.push(canvas.toDataURL("image/png"));
  }

  return images;
};

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  client: Client;
  documentToSign?: DocSignature;
};

export const DocumentToSignModalForm = ({ isOpen, documentToSign, onCancel, client }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const [pdfUrl, setPdfUrl] = useState<string>();
  const [pdfImages, setPdfImages] = useState<string[]>([]);

  const [form] = Form.useForm<InitDocSignature & { file?: RcFile }>();
  const documentType = Form.useWatch("type", form);

  const hasClientAccountsPermission = useHasPermissions(Permission.CLIENT_ACCOUNTS);

  const { data: accounts } = useGetClientUserAccounts(hasClientAccountsPermission ? client.id : undefined);
  const resendSignatureNotification = useResendDocumentSignature();
  const createDocumentSignature = useCreateDocumentSignature();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    form.setFieldsValue({ clientOptimisticLockVersion: client.optimisticLockVersion });

    if (documentToSign) {
      form.setFieldsValue({
        optimisticLockVersion: documentToSign.optimisticLockVersion,
      });
    }
  }, [isOpen, documentToSign, client]);

  useEffect(() => {
    if (pdfUrl) {
      loadPdfAsImages(pdfUrl).then(setPdfImages);
    }
  }, [pdfUrl]);

  const handleBeforeUpload = (file: RcFile) => {
    if (!HttpContentType.PDF.includes(file.type)) {
      notification.error({ title: t("common.error"), description: t("helpers.onlyPdfSupported") });
      return Upload.LIST_IGNORE;
    }

    setPdfUrl(URL.createObjectURL(file));

    return false;
  };

  const handleFormSubmit = () => {
    form.validateFields().then(({ file, ...values }) => {
      if (file) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("dto", convertObjectToBlob(values));

        createDocumentSignature.mutate(
          { id: client.id, object: formData },
          { onSuccess: onCancel, onError: (error) => setErrorsToForm(form, "documentSign.attrs", error.violations) },
        );
      } else {
        resendSignatureNotification.mutate(
          { id1: client.id, id2: documentToSign?.id ?? "", object: values },
          { onSuccess: onCancel, onError: (error) => setErrorsToForm(form, "documentSign.attrs", error.violations) },
        );
      }
    });
  };

  return (
    <Modal
      width={ModalSizes.SMALL}
      open={isOpen}
      onCancel={onCancel}
      title={
        documentToSign ? t("documentSign.titles.resendDocumentToSign") : t("documentSign.titles.sendDocumentToSign")
      }
      confirmLoading={createDocumentSignature.isPending || resendSignatureNotification.isPending}
      cancelText={t("common.cancel")}
      okText={t("common.send")}
      afterClose={() => {
        form.resetFields();
        setPdfUrl(undefined);
      }}
      onOk={handleFormSubmit}
    >
      <Form form={form} layout="vertical" name="createUpdateDocSignature">
        <Form.Item name="type" label={t("documentSign.enums.docSignatureType._label")} rules={[validations.notNull]}>
          <Select
            options={Object.values(DocSignatureType).map((type) => ({
              value: type,
              label: t(`documentSign.enums.docSignatureType.${type}`),
            }))}
          />
        </Form.Item>

        {documentType === DocSignatureType.EMAIL && (
          <>
            <ContactsEmailAutoComplete
              formItemProps={{
                name: "clientEmail",
                label: t("documentSign.attrs.clientEmail"),
                rules: [validations.notBlank, validations.size(1, 254), validations.email],
              }}
              contacts={client.contacts}
            />

            <HiddenInput name="clientOptimisticLockVersion" initialValue={client.optimisticLockVersion} />
          </>
        )}

        {documentType === DocSignatureType.MOBILE_APP && (
          <Form.Item
            name="clientAccountId"
            label={t("documentSign.attrs.clientAccountId")}
            rules={[validations.notNull]}
          >
            <Select
              options={accounts?.map((account) => ({
                value: account.id,
                label: `${account.user.name} ${account.disabled ? `(${t("documentSign.helpers.accountDisabled")})` : ``} ${account.confirmed ? `` : `(${t("documentSign.helpers.accountNotConfirmed")}) ${account.user.locked ? `(${t("documentSign.helpers.userLocked")})` : ``}`}`,
                disabled: account.disabled || !account.confirmed || account.user.locked,
              }))}
            />
          </Form.Item>
        )}

        {documentToSign ? (
          <HiddenInput name="optimisticLockVersion" initialValue={documentToSign.optimisticLockVersion} />
        ) : (
          <Form.Item
            name="file"
            getValueFromEvent={({ file }: { file: UploadFile | RcFile }) => {
              if ("status" in file && file.status === "removed") {
                setPdfUrl(undefined);
                return;
              }

              return file;
            }}
            rules={[validations.notNull]}
            valuePropName="file"
          >
            <Upload maxCount={1} accept={FileUploadAccept.PDF} beforeUpload={handleBeforeUpload}>
              <ActionButton icon="plus">
                <span className="form-item-required-after">{t("common.chooseFile")}</span>
              </ActionButton>
            </Upload>
          </Form.Item>
        )}

        {pdfUrl && (
          <Card title={t("documentSign.titles.preview")} size="small">
            <div style={{ height: 380, overflow: "auto" }}>
              {Array.from({ length: pdfImages.length }, (_, i) => i + 1).map((pageNumber) => (
                <img
                  key={pageNumber}
                  src={pdfImages[pageNumber - 1]}
                  alt={`Page ${pageNumber}`}
                  style={{ width: "100%", borderBottom: "1px solid grey" }}
                />
              ))}
            </div>
          </Card>
        )}
      </Form>
    </Modal>
  );
};
